import { useContext } from 'react'
import { format } from 'date-fns'
import { RouterContext } from 'router'

import { companyDetails, editDetails } from '@pepper/utils/src/api/users/users'
import {
  getCategoryBaseProjectStatus,
  onlyUnique,
  getShouldDisableField,
} from '@pepper/utils/src/function/misc'

import { QueryDetailsType } from 'utils/hooks/useTypeformQueryParams'

export const getNumberChipsValue = (
  data: DashboardMetrics[] | undefined = [],
  key = ''
): number => {
  let value = 0
  const record = data.find(d => d.name === key)
  value = record ? record.value || 0 : 0
  return value
}

export const getProjectCSVData = (records: Project[] = []): string[][] => {
  const headers: string[][] = [
    [
      'Project Code',
      'Project Title',
      'Genre',
      'Vertical',
      'Language',
      'Status',
      'Date Created',
    ],
  ]

  records.forEach(r => {
    const row: string[] = [
      r.project_code.toString(),
      r.project_title,
      r.genre?.name ?? '',
      r.vertical?.name,
      r.language?.name ?? '',
      getCategoryBaseProjectStatus(r.category?.key ?? 'text', r.status)
        .client_title,
      format(new Date(r.created_at), 'dd-MM-yyyy'),
    ]
    headers.push(row)
  })

  return headers
}

export const getAssignmentCSVData = (
  records: Assignment[] = []
): string[][] => {
  const headers: string[][] = [
    [
      'Assign. Code',
      'Assignment topic',
      'Word Count',
      'Last Submission',
      'File Url',
    ],
  ]

  records.forEach(r => {
    const row: string[] = [
      r.assignment_code.toString(),
      r.assignment_title,
      r.client_word_count,
      r.last_submission_date
        ? format(new Date(r?.last_submission_date), 'dd-MM-yyyy')
        : '',
      r?.submission_file?.file_url &&
      ['approved', 'finished', 'rejected', 'rework'].includes(r.stage)
        ? r?.submission_file?.file_url
        : '',
    ]
    headers.push(row)
  })

  return headers
}

const _formItemData = (
  companyId: string,
  records: Item[]
): Record<string, string | number>[] => {
  const data: Record<string, string | number>[] = []
  records.forEach(r => {
    const res = {
      'Item Code': r.code.toString(),
      'Item Topic': r.title,
      'Word Count': r.client_word_count,
      'File Url': `${window.location.origin}/${companyId}/items/${r.id}`,
    }
    data.push(res)
  })
  return data
}

const getPublicFileUrl = (r: Assignment, companyId: string) => {
  return r?.submission_file?.is_public
    ? `${window.location.origin}/d/${r.id}`
    : `${window.location.origin}/${companyId}/document/${r.id}`
}

const getFileUrlFromResponse = (r: Assignment) => {
  return r?.submission_file?.file_url &&
    ['approved', 'finished', 'rejected', 'rework'].includes(r.stage)
    ? r?.submission_file?.file_url
    : ''
}

const getFileUrl = (r: Assignment, companyId: string) => {
  return r?.submission_file?.src_type === 'pepper_editor' &&
    ['approved', 'finished', 'rejected', 'rework'].includes(r.stage)
    ? getPublicFileUrl(r, companyId)
    : getFileUrlFromResponse(r)
}

const _formAssignmentData = (
  companyId: string,
  records: Assignment[]
): Record<string, string | number>[] => {
  const data: Record<string, string | number>[] = []
  records.forEach(r => {
    const res = {
      'Item Code': r.assignment_code.toString(),
      'Item Topic': r.assignment_title,
      'Word Count': r.client_word_count,
      'Last Submission': r?.last_submission_date
        ? format(new Date(r?.last_submission_date), 'dd-MM-yyyy')
        : '',
      'File Url': getFileUrl(r, companyId),
    }
    data.push(res)
  })

  return data
}

export const getPapaUnparseAssignmentData = (
  companyId: string,
  records: Assignment[] = []
): Record<string, string | number>[] => {
  return _formAssignmentData(companyId, records)
}

export const getPapaUnparseItemsData = (
  companyId: string,
  records: Item[] = []
): Record<string, string | number>[] => {
  return _formItemData(companyId, records)
}

export const areWordCountValid = (
  assignments: AssignmentListFormValues[]
): boolean => {
  const result: boolean = assignments.every(value => {
    if (typeof value.word_count === 'undefined') return false

    return Number.parseInt(value.word_count) > 0
  })

  return result
}

export const createCompanyAndUpdateUser = async (
  userId: string | null,
  companyName: string
): Promise<ApiResponse> => {
  // Create new company and set company_setup_done as true
  const response = await companyDetails({
    company_name: companyName,
  })
  await editDetails({
    id: userId,
    body: {
      company_setup_done: true,
    },
  })

  return response
}

export const checkIsValidCompanyId = (
  companyId: string,
  user: User
): boolean => {
  const result = user.companies.findIndex(c => c === companyId) !== -1

  return result
}

export const useSelectedCompanyId = (): string => {
  const { companyId } = useContext(RouterContext)

  return companyId
}

interface Language {
  id: number
  title: string
  copy_test_file_url?: string
}

export const getLanguageById = (
  languages: Language[],
  languageId: number
): Language | null => {
  return languages.find(language => language.id === languageId) ?? null
}

export const getUserCompanySetupComplete = (
  user: User,
  companyId: string
): boolean => {
  if (!(companyId && user?.company_details?.length)) {
    return false
  }
  const activeCompany = user.company_details.find(item => item.id === companyId)
  return (
    Boolean(user?.profile?.company_setup_done) &&
    Boolean(user?.company_name) &&
    activeCompany?.company_name !== 'Unnamed Organisation'
  )
}

// Convert a sentence into a Title case string
export const getTitleCase = (str: string): string => {
  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const isFieldValuePresent = (fiedValue: unknown): boolean => {
  if ([null, undefined, ''].includes(fiedValue as any)) return false
  if (Array.isArray(fiedValue) && fiedValue.length === 0) return false
  return true
}

const getFieldValueFromAssignments = (
  assignments: Assignment[],
  fetchValueFromAssignment: (assignment: Assignment) => any,
  notFoundValue?: any
): any => {
  // TODO: enhance this to consider root assignment here for grouped assignments, just to be safer
  let finalFieldValue
  const isValueFoundInSomeAssignment = assignments.some(assignment => {
    const valueInAssignment = fetchValueFromAssignment(assignment)
    const isValuePresent = isFieldValuePresent(valueInAssignment)
    if (isValuePresent) finalFieldValue = valueInAssignment
    return isValuePresent
  })
  return isValueFoundInSomeAssignment ? finalFieldValue : notFoundValue
}

export const getDraftBodyFromProjectDetails = ({
  projectDetails,
  companyId,
  assignments,
  company,
  fields,
  keySetContainingTagField,
  rootVerticalId,
}: {
  projectDetails: Project
  companyId: string
  assignments: Assignment[]
  company: Company
  fields: BackendFormField[]
  keySetContainingTagField: Record<
    string,
    {
      tag: string
      tagField: string
      isAssignment?: boolean
    }
  >
  rootVerticalId?: number
}): DraftPayload => {
  const assignmentLevelTags = Object.values(keySetContainingTagField).reduce(
    (tagsList: string[], tagSet) => {
      if (tagSet.isAssignment) tagsList.push(tagSet.tag)
      return tagsList
    },
    []
  )
  const otherLinks = assignments
    .map(assignment => assignment?.other_links ?? [])
    .flat()
    .filter(onlyUnique)
    .map((item: string) => {
      const splitItem = item?.split(',')
      return {
        tag: splitItem?.[0]?.split('tag:')?.[1] ?? '',
        link: splitItem?.[1]?.split('link:')?.[1] ?? '',
      }
    })
    .filter(
      otherLinkObject =>
        otherLinkObject.tag &&
        !assignmentLevelTags.includes(otherLinkObject.tag)
    )

  const referenceFiles = assignments
    .map(assignment => assignment?.reference_files ?? [])
    .flat()
    .map(file => `id:${file.id},name:${file.name},tag:${file.tag}`)
    .filter(onlyUnique)
    .map((item: string) => {
      const splitItem = item?.split(',')
      return {
        id: splitItem?.[0]?.split('id:')?.[1] ?? '',
        name: splitItem?.[1]?.split('name:')?.[1] ?? '',
        tag: splitItem?.[2]?.split('tag:')?.[1] ?? '',
      }
    })
    .filter(file => file.tag && !assignmentLevelTags.includes(file.tag))

  const draftBody: Record<string, any> = {}

  fields.forEach(field => {
    if (['company_name', 'company_bio'].includes(field.id)) {
      draftBody[field.id] = (company as any)[field.id]
      return
    }

    if (field.id === 'primary_industry') {
      draftBody.primary_industry = (company as any).primary_industry?.id
      return
    }

    if (field.id === 'translation_languages_target_id') {
      draftBody.translation_languages_target_id =
        projectDetails.translation_languages_target?.map(
          translation_languages_target => translation_languages_target.id
        )
      return
    }

    if (field.id === 'project_goals') {
      draftBody.project_goals = getFieldValueFromAssignments(
        assignments,
        assignment => assignment?.project_goals?.map(goal => goal.id)
      )
      return
    }

    if (field.id === 'tone_of_voice') {
      draftBody.tone_of_voice = getFieldValueFromAssignments(
        assignments,
        assignment => assignment?.tone_of_voice?.map(tone => tone.id)
      )
      return
    }

    if (field.id === 'platforms') {
      draftBody.platforms = getFieldValueFromAssignments(
        assignments,
        assignment =>
          assignment?.platforms?.map(platform => {
            if (typeof platform === 'string') return platform

            return platform.name
          })
      )
      return
    }

    if (field.id === 'meta_data') {
      draftBody.meta_data = Boolean(
        getFieldValueFromAssignments(
          assignments,
          assignment => assignment?.meta_data
        )
      )
      return
    }

    if (field.id === 'references') {
      draftBody[field.id] = referenceFiles.filter(file => !file.tag)
      return
    }

    if (field.id === 'primary_keywords' || field.id === 'other_keywords') {
      return
    }

    const value = getFieldValueFromAssignments(
      assignments,
      assignment => assignment?.[field.id],
      ''
    )

    if (field.id === 'sub_genre_id' && value === '') {
      return
    }

    if (
      field.type === 'html' &&
      (typeof value === 'string' || !value) &&
      keySetContainingTagField?.[field.id]
    ) {
      const matchedTag = keySetContainingTagField?.[field.id]?.tag
      const _value = {
        richText: typeof value === 'string' && value ? value : '',
        attachments: referenceFiles?.filter(file => file.tag === matchedTag),
        loomUrls: otherLinks
          .filter(
            (otherLinkItem: { tag: string }) =>
              otherLinkItem?.tag === matchedTag
          )
          .map((it: { link: string }) => it?.link),
      }
      draftBody[field.id] = _value
      return
    }

    const isCommentBoxField = field.isAttachFile && !field.isVideoRecord
    if (
      field.type === 'comment-box' &&
      (typeof value === 'string' || !value) &&
      isCommentBoxField
    ) {
      const matchedTagField = keySetContainingTagField?.[field.id]?.tagField
      const tagFieldValue = getFieldValueFromAssignments(
        assignments,
        assignment => assignment?.[matchedTagField],
        ''
      )
      draftBody[matchedTagField] = tagFieldValue
      return
    }

    draftBody[field.id] = value
  })

  draftBody.other_links = otherLinks.map(
    (otherLinkObject: { tag: string; link: string }) =>
      `tag:${otherLinkObject.tag},link:${otherLinkObject.link}`
  )
  draftBody.reference_files = referenceFiles

  const rootAssignment = assignments.find(
    assignment => assignment.vertical.id === rootVerticalId
  )
  draftBody.project_title = `Copy of ${rootAssignment?.project.title}`
  draftBody.language_id = rootAssignment?.language_id
  draftBody.vertical_id = rootAssignment?.vertical.id
  draftBody.project_category = rootAssignment?.category_id
  draftBody.document_brief_id = assignments[0]?.document_brief?.id

  draftBody.experience_level =
    String(rootAssignment?.experience_level?.id) ?? ''
  const nonRootVerticals = projectDetails.verticals.reduce(
    (verticals: { id: number; categoryId?: number }[], vertical) => {
      if (rootVerticalId !== vertical.id)
        verticals.push({ id: vertical.id, categoryId: vertical.category_id })
      return verticals
    },
    []
  )
  nonRootVerticals.forEach(vertical => {
    let fieldId = ''
    if (vertical.categoryId === 3) fieldId = 'experience_level_translation'
    if (vertical.categoryId === 2) fieldId = 'experience_level_design'
    if (vertical.categoryId === 1) fieldId = 'experience_level_text'
    draftBody[fieldId] = String(
      getFieldValueFromAssignments(
        assignments,
        assignment => {
          return vertical.id === assignment.vertical.id
            ? assignment?.experience_level?.id
            : null
        },
        ''
      )
    )
  })

  draftBody.company_id = companyId
  draftBody.duplicate_project_id = projectDetails.id

  return draftBody as DraftPayload
}

export const filterCreateProjectJsonByAccountType = (
  data: IProjectFlowResponse,
  accountType: string
): IProjectFlowResponse => {
  const filteredResponse = data.response.filter(subData => {
    return (
      (accountType === 'enterprise' && subData.filter !== 'standard') ||
      (accountType === 'standard' && subData.filter !== 'enterprise')
    )
  })
  return {
    ...data,
    response: filteredResponse,
  }
}

export const getDraftBodyFromTypeformQueryParams = (
  company: any,
  queryParamDetails: QueryDetailsType | null
): Record<string, any> => {
  const draftBody: Record<string, any> = {}
  draftBody.project_title = `${company?.company_name}'s Project`
  draftBody.vertical_id = queryParamDetails?.vertical
  draftBody.genre_id = queryParamDetails?.genre
  draftBody.experience_level = String(queryParamDetails?.experience ?? '')
  draftBody.project_category = queryParamDetails?.projectCategory
  draftBody.word_count = queryParamDetails?.wordCount
  draftBody.company_id = company.id
  Object.keys(draftBody).forEach(key => {
    if (!draftBody[key]) {
      delete draftBody[key]
    }
  })
  return draftBody
}

function isCommentBoxField(
  fieldJson: unknown,
  value: unknown
): value is { loomUrls?: unknown; richText?: unknown } {
  const isCommentFieldObject = (
    _value: unknown
  ): _value is Record<'isAttachFile' | 'isVideoRecord', unknown> => {
    return (
      !!fieldJson &&
      typeof fieldJson === 'object' &&
      'isAttachFile' in fieldJson &&
      'isVideoRecord' in fieldJson
    )
  }
  return (
    isCommentFieldObject(fieldJson) &&
    !!fieldJson.isAttachFile &&
    !fieldJson.isVideoRecord
  )
}

export const getShouldDisableFieldForCurrentStep = (
  groupItem: IProjectBody,
  valuesForPreFilling: Record<string, unknown>,
  fieldsWithValueFromDefaultStaticValueJsonkey: string[]
): boolean => {
  // Project title shouldn't be editable for add assignment and item drafts flow
  if (['project_title'].includes(groupItem.id)) return true

  // We have decided to disable prefilling for this field for now
  // We will re-enable it once we have updated the json across verticals to have same options
  if (['design_style_to_follow', 'design_visual_tone'].includes(groupItem.id))
    return false
  // 'I am an individual' field in company details step
  // This can be handled in a more robust manner
  if (
    fieldsWithValueFromDefaultStaticValueJsonkey.length === 1 &&
    fieldsWithValueFromDefaultStaticValueJsonkey[0] === 'individual'
  )
    return true
  // don't disable the field if value didn't come from profiler service
  if (fieldsWithValueFromDefaultStaticValueJsonkey.includes(groupItem.id))
    return false
  const value = valuesForPreFilling[groupItem.id]
  if (isCommentBoxField(groupItem, value)) {
    value.loomUrls && delete value.loomUrls
    value.richText && delete value.richText
  }
  return getShouldDisableField(value)
}
